import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControlLabel,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { map } from 'es-toolkit/compat';
import React from 'react';

import { CaseOrganizationExecutionMode, caseOrganizationHandlingLabels } from './caseOrganizationPlan';
import { ManifestSurface } from './dialogState';

export type ManifestWorkspaceSurface = ManifestSurface | 'Cross Lab Search';

interface CaseManifestSlideSourceMenuProps {
  anchorEl: HTMLElement | null;
  entries: { id: ManifestWorkspaceSurface; label: string }[];
  isChoosingEntryIntent: boolean;
  label: string;
  onChange: (surface: ManifestWorkspaceSurface) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  selectedSurface: ManifestWorkspaceSurface;
  setAnchorEl: (element: HTMLButtonElement | null) => void;
}

export const CaseManifestSlideSourceMenu = ({
  anchorEl,
  entries,
  isChoosingEntryIntent,
  label,
  onChange,
  onOpenChange,
  open,
  selectedSurface,
  setAnchorEl,
}: CaseManifestSlideSourceMenuProps) => (
  <>
    <Button
      ref={setAnchorEl}
      variant={isChoosingEntryIntent ? 'contained' : 'outlined'}
      color={isChoosingEntryIntent ? 'primary' : 'inherit'}
      endIcon={<ArrowDropDownIcon />}
      onClick={() => onOpenChange(true)}
      disabled={entries.length === 0}
      aria-controls={open ? 'select-slides-menu' : undefined}
      aria-haspopup="menu"
      aria-expanded={open ? 'true' : undefined}
    >
      {isChoosingEntryIntent ? 'Add more slides to the study' : label}
    </Button>
    <Menu
      id="select-slides-menu"
      anchorEl={anchorEl}
      open={open && Boolean(anchorEl)}
      onClose={() => onOpenChange(false)}
    >
      {map(entries, (entry) => (
        <MenuItem key={entry.id} selected={entry.id === selectedSurface} onClick={() => onChange(entry.id)}>
          {entry.label}
        </MenuItem>
      ))}
    </Menu>
  </>
);

interface CaseManifestHandlingProps {
  disabled: boolean;
  executionMode: CaseOrganizationExecutionMode;
  expanded: boolean;
  isExecutionModeValid: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onExpandedChange: (expanded: boolean) => void;
}

export const CaseManifestHandling = ({
  disabled,
  executionMode,
  expanded,
  isExecutionModeValid,
  onChange,
  onExpandedChange,
}: CaseManifestHandlingProps) => (
  <Accordion disableGutters expanded={expanded} onChange={(_event, nextExpanded) => onExpandedChange(nextExpanded)}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="existing-case-handling-content">
      <Typography variant="body2">
        Existing case handling:{' '}
        {isExecutionModeValid ? caseOrganizationHandlingLabels[executionMode] : 'Choose an operation'}
      </Typography>
    </AccordionSummary>
    <AccordionDetails id="existing-case-handling-content">
      <RadioGroup
        aria-label="Existing case handling"
        value={isExecutionModeValid ? executionMode : ''}
        onChange={onChange}
      >
        {map(caseOrganizationHandlingLabels, (label, mode) => (
          <FormControlLabel key={mode} value={mode} control={<Radio />} label={label} disabled={disabled} />
        ))}
      </RadioGroup>
      {executionMode === 'routine' && (
        <Typography variant="body2" color="text.secondary">
          Slides not included in this plan remain in their current cases.
        </Typography>
      )}
    </AccordionDetails>
  </Accordion>
);

interface CaseManifestEntryChoicesProps {
  canReviewUnassigned: boolean;
  caseListLoadingIcon: React.ReactNode;
  existingCasesDisabled: boolean;
  isCasesError: boolean;
  isCasesLoading: boolean;
  onReviewExisting: () => void;
  onReviewUnassigned: () => void;
  selectSlidesControl: React.ReactNode;
  studySourceStatus: 'loading' | 'error' | null;
  unassignedStudySlideCount: number;
}

export const CaseManifestEntryChoices = ({
  canReviewUnassigned,
  caseListLoadingIcon,
  existingCasesDisabled,
  isCasesError,
  isCasesLoading,
  onReviewExisting,
  onReviewUnassigned,
  selectSlidesControl,
  studySourceStatus,
  unassignedStudySlideCount,
}: CaseManifestEntryChoicesProps) => (
  <Box>
    <Box sx={{ display: 'flex', gap: 2, '& > .MuiButton-root': { flex: 1, minWidth: 0, minHeight: 44 } }}>
      {canReviewUnassigned && (
        <Button color="primary" variant="contained" onClick={onReviewUnassigned}>
          Review {unassignedStudySlideCount} study lost and found slides
        </Button>
      )}
      <Button
        color="primary"
        variant="contained"
        aria-busy={isCasesLoading}
        disabled={existingCasesDisabled}
        startIcon={caseListLoadingIcon}
        onClick={onReviewExisting}
      >
        Review existing case assignments
      </Button>
      {selectSlidesControl}
    </Box>
    {studySourceStatus && (
      <Typography role="status" variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        {studySourceStatus === 'loading'
          ? 'Loading all study lost and found slides...'
          : 'Could not load all study lost and found slides. Please contact support.'}
      </Typography>
    )}
    {isCasesError && (
      <Typography role="status" variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Could not load all study cases. Please contact support.
      </Typography>
    )}
  </Box>
);
